import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import LcSblcPage from './pages/LcSblcPage';
import BillOfLadingPage from './pages/BillOfLadingPage';
import PaymentTermsPage from './pages/PaymentTermsPage';
import DemurrageCalculatorPage from './pages/DemurrageCalculatorPage';
import IncotermManagementPage from './pages/IncotermManagementPage';
import TradeFinancePlatformPage from './pages/TradeFinancePlatformPage';
import UserManagementPage from './pages/UserManagementPage';
import CounterpartyPage from './pages/CounterpartyPage';
import DealsPage from './pages/DealsPage';
import AuditLogPage from './pages/AuditLogPage';
import AuditChainPage from './pages/AuditChainPage';
import AlertsPage from './pages/AlertsPage';
import VesselPage from './pages/VesselPage';
import PLPage from './pages/PLPage';
import RiskRegisterPage from './pages/RiskRegisterPage';
import InsurancePage from './pages/InsurancePage';
import InspectionPage from './pages/InspectionPage';
import ShipTrackingPage from './pages/ShipTrackingPage';
import CommodityPricePage from './pages/CommodityPricePage';
import SwiftParserPage from './pages/SwiftParserPage';
import ExternalPortalPage from './pages/ExternalPortalPage';
import DocumentsPage from './pages/DocumentsPage';

// Global axios instance
const API = axios.create({ baseURL: '/api', timeout: 30000 });
export function getToken() {
  try {
    return JSON.parse(localStorage.getItem('energy_auth') || 'null')?.access_token || '';
  } catch { return ''; }
}
export function authHdr() { return { Authorization: `Bearer ${getToken()}` }; }
API.interceptors.request.use(cfg => {
  const t = getToken();
  if (t) cfg.headers['Authorization'] = `Bearer ${t}`;
  return cfg;
});
API.interceptors.response.use(
  res => res,
  err => {
    if (err.response && err.response.status === 401) {
      localStorage.removeItem('energy_auth');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);
export { API };

// i18n
const TRANSLATIONS = {
  de: {
    dashboard: 'Dashboard', deals: 'Deals', counterparties: 'Counterparties',
    trade_finance: 'Trade Finance', lc_sblc: 'LC / SBLC', bill_of_lading: 'Bill of Lading',
    payment_terms: 'Payment Terms', demurrage: 'Demurrage', incoterms: 'Incoterms',
    vessels: 'Vessel Tracking', insurance: 'Versicherungen', inspections: 'Inspektionen',
    pl: 'P&L Tracking', risks: 'Risk Register',
    commodity_prices: 'Rohstoffpreise', ship_tracking: 'Ship Tracking', swift_parser: 'SWIFT Parser',
    users: 'Benutzerverwaltung', alerts: 'Alerts', audit: 'Audit Log',
    logout: 'Abmelden',
  },
  en: {
    dashboard: 'Dashboard', deals: 'Deals', counterparties: 'Counterparties',
    trade_finance: 'Trade Finance', lc_sblc: 'LC / SBLC', bill_of_lading: 'Bill of Lading',
    payment_terms: 'Payment Terms', demurrage: 'Demurrage', incoterms: 'Incoterms',
    vessels: 'Vessel Tracking', insurance: 'Insurance', inspections: 'Inspections',
    pl: 'P&L Tracking', risks: 'Risk Register',
    commodity_prices: 'Commodity Prices', ship_tracking: 'Ship Tracking', swift_parser: 'SWIFT Parser',
    users: 'User Management', alerts: 'Alerts', audit: 'Audit Log',
    logout: 'Sign out',
  },
};
export function t(lang, key) { return TRANSLATIONS[lang]?.[key] || TRANSLATIONS.de[key] || key; }

// Light theme CSS override
const LIGHT_CSS = `
  body.light-theme {
    --bg-app:      #f0f4f0;
    --bg-deep:     #e4ece4;
    --bg-surface:  #f8faf8;
    --bg-card:     #ffffff;
    --bg-hover:    #e8f0e8;
    --border:      #c8d8c8;
    --text1:       #1a2e1a;
    --text2:       #3a5a3a;
    --text3:       #6a8a6a;
  }
`;

// CSS
const CSS = `
  :root {
    --bg-app:      #0a0f0a;
    --bg-deep:     #0d130d;
    --bg-surface:  #141f14;
    --bg-card:     #192419;
    --bg-hover:    #1e2e1e;
    --border:      #2a3d2a;
    --text1:       #e0f0e0;
    --text2:       #a0bca0;
    --text3:       #607060;
    --accent:      #22c55e;
    --accent-dim:  #16a34a;
    --accent-glow: #22c55e20;
    --danger:      #ef4444;
    --warn:        #f59e0b;
    --info:        #3b82f6;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--bg-app); color: var(--text1); font-family: system-ui, sans-serif; font-size: 14px; }

  .app-layout { display: flex; height: 100vh; overflow: hidden; }

  .sidebar {
    width: 220px; min-width: 220px;
    background: var(--bg-deep);
    border-right: 1px solid var(--border);
    display: flex; flex-direction: column;
    overflow-y: auto;
  }
  .sidebar-logo {
    padding: 20px 16px 12px;
    font-size: 13px; font-weight: 800; letter-spacing: .08em;
    color: var(--accent); border-bottom: 1px solid var(--border);
  }
  .sidebar-logo span { display: block; font-size: 10px; font-weight: 400; color: var(--text3); margin-top: 2px; }
  .sidebar-section { padding: 12px 12px 4px; font-size: 10px; font-weight: 700; color: var(--text3); letter-spacing: .12em; text-transform: uppercase; }
  .sidebar a {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 16px; font-size: 13px; color: var(--text2);
    text-decoration: none; border-left: 2px solid transparent;
    transition: all .15s;
  }
  .sidebar a:hover { background: var(--bg-hover); color: var(--text1); }
  .sidebar a.active { background: var(--accent-glow); color: var(--accent); border-left-color: var(--accent); font-weight: 600; }

  .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
  .topbar {
    height: 48px; background: var(--bg-deep);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: flex-end;
    padding: 0 20px; gap: 12px; flex-shrink: 0;
  }
  .topbar-user { font-size: 12px; color: var(--text3); }

  .page-content { flex: 1; overflow-y: auto; padding: 24px; }
  .page-header { margin-bottom: 24px; }
  .page-header h1 { font-size: 22px; font-weight: 700; color: var(--text1); }
  .page-header p { font-size: 13px; color: var(--text3); margin-top: 4px; }

  .card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 16px;
  }
  .card-title {
    font-size: 13px; font-weight: 700; color: var(--text1);
    margin-bottom: 12px; display: flex; align-items: center; gap: 8px;
  }

  .btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 14px; border-radius: 7px; font-size: 13px; font-weight: 600;
    border: none; cursor: pointer; transition: all .15s;
  }
  .btn-primary { background: var(--accent); color: #000; }
  .btn-primary:hover { background: #16a34a; }
  .btn-ghost { background: transparent; color: var(--text2); border: 1px solid var(--border); }
  .btn-ghost:hover { background: var(--bg-hover); color: var(--text1); }
  .btn-danger { background: #ef444420; color: var(--danger); border: 1px solid #ef444440; }
  .btn-danger:hover { background: #ef444430; }
  .btn-sm { padding: 5px 10px; font-size: 12px; }

  .form-group { margin-bottom: 14px; }
  .form-label { display: block; font-size: 12px; font-weight: 600; color: var(--text2); margin-bottom: 5px; }
  .form-input {
    width: 100%; padding: 8px 12px; border-radius: 7px;
    background: var(--bg-surface); border: 1px solid var(--border);
    color: var(--text1); font-size: 13px;
    transition: border-color .15s;
  }
  .form-input:focus { outline: none; border-color: var(--accent); }
  select.form-input { cursor: pointer; }
  textarea.form-input { resize: vertical; min-height: 80px; }

  .badge {
    display: inline-flex; align-items: center;
    padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;
  }
  .badge-green  { background: #22c55e20; color: #22c55e; }
  .badge-yellow { background: #f59e0b20; color: #f59e0b; }
  .badge-red    { background: #ef444420; color: #ef4444; }
  .badge-blue   { background: #3b82f620; color: #3b82f6; }
  .badge-gray   { background: #60706020; color: var(--text3); }

  .tbl { width: 100%; border-collapse: collapse; }
  .tbl th { text-align: left; padding: 8px 12px; font-size: 11px; font-weight: 700; color: var(--text3); border-bottom: 1px solid var(--border); text-transform: uppercase; }
  .tbl td { padding: 10px 12px; border-bottom: 1px solid var(--border); font-size: 13px; vertical-align: middle; }
  .tbl tr:hover td { background: var(--bg-hover); }

  .modal-overlay {
    position: fixed; inset: 0; background: #00000080;
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 20px;
  }
  .modal {
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: 12px; padding: 24px;
    width: 100%; max-width: 600px; max-height: 90vh; overflow-y: auto;
  }
  .modal-title { font-size: 16px; font-weight: 700; margin-bottom: 16px; }
  .modal-footer { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }

  .stat-card { text-align: center; padding: 20px; }
  .stat-card .stat-value { font-size: 36px; font-weight: 800; color: var(--accent); }
  .stat-card .stat-label { font-size: 12px; color: var(--text3); margin-top: 4px; }

  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  @media (max-width: 768px) {
    .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr; }
    .sidebar { width: 48px; min-width: 48px; }
    .sidebar a span:last-child { display: none; }
    .sidebar-logo span, .sidebar-section { display: none; }
  }

  .loading { color: var(--text3); font-size: 13px; padding: 20px; text-align: center; }
  .error-msg { padding: 10px 14px; background: #ef444415; border: 1px solid #ef444440; border-radius: 7px; color: var(--danger); font-size: 13px; margin-bottom: 12px; }
`;

function AppLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const [lang, setLang]   = useState(() => localStorage.getItem('bpos_lang') || 'de');
  const [theme, setTheme] = useState(() => localStorage.getItem('bpos_theme') || 'dark');

  // Apply theme class to body
  React.useEffect(() => {
    if (theme === 'light') document.body.classList.add('light-theme');
    else                   document.body.classList.remove('light-theme');
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('bpos_theme', next);
  };

  const toggleLang = () => {
    const next = lang === 'de' ? 'en' : 'de';
    setLang(next);
    localStorage.setItem('bpos_lang', next);
  };

  const handleLogout = () => {
    localStorage.removeItem('energy_auth');
    onLogout();
    navigate('/login');
  };

  const NAV = [
    { section: 'Übersicht' },
    { to: '/', label: 'Dashboard', icon: '📊' },
    { to: '/deals', label: 'Deals', icon: '📁' },
    { to: '/counterparties', label: 'Counterparties', icon: '🏢' },
    { to: '/documents', label: 'Planungsdokumente', icon: '🗂' },
    { section: 'Trade Finance' },
    { to: '/trade-finance', label: 'Trade Finance', icon: '🌐' },
    { to: '/lc-sblc', label: 'LC / SBLC', icon: '🏦' },
    { to: '/bill-of-lading', label: 'Bill of Lading', icon: '🚢' },
    { to: '/payment-terms', label: 'Payment Terms', icon: '💳' },
    { section: 'Kalkulation' },
    { to: '/demurrage', label: 'Demurrage', icon: '⚓' },
    { to: '/incoterms', label: 'Incoterms', icon: '📋' },
    { section: 'Operationen' },
    { to: '/vessels', label: 'Vessel Tracking', icon: '⛴' },
    { to: '/insurance', label: 'Versicherungen', icon: '🛡' },
    { to: '/inspections', label: 'Inspektionen', icon: '🔬' },
    { section: 'Finanzen' },
    { to: '/pl', label: 'P&L Tracking', icon: '📊' },
    { to: '/risks', label: 'Risk Register', icon: '⚠' },
    { section: 'Market Data' },
    { to: '/commodity-prices', label: 'Rohstoffpreise', icon: '📈' },
    { to: '/ship-tracking', label: 'Ship Tracking', icon: '🛳' },
    { to: '/swift-parser', label: 'SWIFT Parser', icon: '📨' },
    { section: 'Admin' },
    { to: '/users', label: 'Benutzerverwaltung', icon: '🔐' },
    { to: '/alerts', label: 'Alerts', icon: '🔔' },
    { to: '/audit', label: 'Audit Log', icon: '📋' },
    { to: '/audit-chain', label: 'Audit Chain', icon: '🔗' },
    { to: '/portal', label: 'External Portal', icon: '🌐' },
  ];

  return (
    <div className="app-layout">
      <style>{CSS}</style>
      <style>{LIGHT_CSS}</style>
      <nav className="sidebar">
        <div className="sidebar-logo">
          ⚡ ENERGY BPOS
          <span>Trade Finance Suite</span>
        </div>
        {NAV.map((item, i) =>
          item.section ? (
            <div key={i} className="sidebar-section">{item.section}</div>
          ) : (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          )
        )}
      </nav>
      <div className="main">
        <div className="topbar">
          <span className="topbar-user">👤 {user?.username} · {user?.role}</span>
          <button className="btn btn-ghost btn-sm" onClick={toggleLang} title="Sprache wechseln">
            {lang === 'de' ? '🇩🇪 DE' : '🇬🇧 EN'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={toggleTheme} title="Theme wechseln">
            {theme === 'dark' ? '☀' : '🌙'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleLogout}>{lang === 'de' ? 'Abmelden' : 'Sign out'}</button>
        </div>
        <div className="page-content">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/lc-sblc" element={<LcSblcPage />} />
            <Route path="/bill-of-lading" element={<BillOfLadingPage />} />
            <Route path="/payment-terms" element={<PaymentTermsPage />} />
            <Route path="/demurrage" element={<DemurrageCalculatorPage />} />
            <Route path="/incoterms" element={<IncotermManagementPage />} />
            <Route path="/trade-finance" element={<TradeFinancePlatformPage />} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/counterparties" element={<CounterpartyPage />} />
            <Route path="/users" element={<UserManagementPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/audit-chain" element={<AuditChainPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/vessels" element={<VesselPage />} />
            <Route path="/pl" element={<PLPage />} />
            <Route path="/risks" element={<RiskRegisterPage />} />
            <Route path="/insurance" element={<InsurancePage />} />
            <Route path="/inspections" element={<InspectionPage />} />
            <Route path="/ship-tracking" element={<ShipTrackingPage />} />
            <Route path="/commodity-prices" element={<CommodityPricePage />} />
            <Route path="/swift-parser" element={<SwiftParserPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/portal" element={<ExternalPortalPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('energy_auth') || 'null')?.user || null; }
    catch { return null; }
  });

  const handleLogin = (data) => {
    localStorage.setItem('energy_auth', JSON.stringify(data));
    setUser(data.user);
  };

  const handleLogout = () => setUser(null);

  return (
    <BrowserRouter>
      {!user ? (
        <>
          <style>{CSS}</style>
          <Routes>
            <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
          </Routes>
        </>
      ) : user?.role === 'external' ? (
        <>
          <style>{CSS}</style>
          <Routes>
            <Route path="*" element={<ExternalPortalPage />} />
          </Routes>
          <div style={{ position: 'fixed', top: 10, right: 16, display: 'flex', gap: 8, zIndex: 999 }}>
            <span style={{ fontSize: 12, color: '#607060', lineHeight: '30px' }}>👤 {user.username}</span>
            <button
              style={{ padding: '5px 12px', borderRadius: 7, background: 'transparent', border: '1px solid #2a3d2a', color: '#a0bca0', fontSize: 12, cursor: 'pointer' }}
              onClick={() => { localStorage.removeItem('energy_auth'); handleLogout(); }}
            >Abmelden</button>
          </div>
        </>
      ) : (
        <AppLayout user={user} onLogout={handleLogout} />
      )}
    </BrowserRouter>
  );
}
